<template>
  <div class="border-background-200 bg-background-50 h-fit rounded-sm border p-4">
    <!-- Header -->
    <div class="bg-background-100/80 mb-4 flex items-center space-x-3 rounded-sm p-1">
      <div class="border-background-400 flex size-10 items-center justify-center rounded-sm border">
        <i class="pi pi-comment text-lg" />
      </div>
      <span class="text-lg font-semibold">Notities</span>
      <span class="text-background-500 text-sm">({{ notities.length }})</span>
    </div>

    <!-- New note form -->
    <div class="border-background-200 bg-background-0 mb-4 rounded-sm border p-3">
      <Textarea
        v-model="draft.inhoud"
        auto-resize
        rows="2"
        class="w-full"
        placeholder="Schrijf een notitie..."
      />
      <div class="mt-2 flex flex-wrap items-center justify-between gap-3">
        <DatePicker
          v-model="draft.reminder_at"
          show-time
          hour-format="24"
          show-button-bar
          date-format="dd-mm-yy"
          placeholder="Herinnering (optioneel)"
          class="w-56"
        />
        <Button
          label="Toevoegen"
          icon="pi pi-plus"
          size="small"
          :loading="saving"
          :disabled="!draft.inhoud.trim()"
          @click="handleCreate"
        />
      </div>
    </div>

    <!-- List -->
    <div
      v-if="loading && notities.length === 0"
      class="text-background-500 py-4 text-center text-sm"
    >
      Laden...
    </div>
    <div
      v-else-if="notities.length === 0"
      class="text-background-500 py-4 text-center text-sm"
    >
      Nog geen notities.
    </div>

    <div
      v-else
      class="flex flex-col gap-3"
    >
      <div
        v-for="notitie in notities"
        :key="notitie.id"
        class="border-background-200 bg-background-0 rounded-sm border p-3"
      >
        <!-- Edit mode (only own notes) -->
        <template v-if="editingId === notitie.id">
          <Textarea
            v-model="editDraft.inhoud"
            auto-resize
            rows="2"
            class="w-full"
          />
          <div class="mt-2 flex flex-wrap items-center justify-between gap-3">
            <DatePicker
              v-model="editDraft.reminder_at"
              show-time
              hour-format="24"
              show-button-bar
              date-format="dd-mm-yy"
              placeholder="Herinnering (optioneel)"
              class="w-56"
            />
            <div class="flex items-center gap-2">
              <Button
                label="Annuleren"
                severity="secondary"
                text
                size="small"
                @click="cancelEdit"
              />
              <Button
                label="Opslaan"
                size="small"
                :loading="saving"
                :disabled="!editDraft.inhoud.trim()"
                @click="handleUpdate(notitie.id)"
              />
            </div>
          </div>
        </template>

        <!-- Display mode -->
        <template v-else>
          <div class="flex items-start justify-between gap-2">
            <p class="text-sm whitespace-pre-wrap break-words">{{ notitie.inhoud }}</p>
            <div
              v-if="isOwn(notitie)"
              class="flex shrink-0 items-center gap-1"
            >
              <Button
                severity="secondary"
                text
                rounded
                size="small"
                icon="pi pi-pencil"
                aria-label="Bewerken"
                @click="startEdit(notitie)"
              />
              <Button
                severity="danger"
                text
                rounded
                size="small"
                icon="pi pi-trash"
                aria-label="Verwijderen"
                @click="confirmDelete(notitie)"
              />
            </div>
          </div>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <CustomTag
              :color="notitie.auteur_type === 'ibedrijf' ? 'verzonden' : 'rol_aanvrager'"
              :label="notitie.auteur_type === 'ibedrijf' ? 'Inspectiebedrijf' : 'U'"
            />
            <CustomTag
              v-if="notitie.reminder_at"
              color="warning"
              icon="pi pi-bell"
              :label="formatDateTime(notitie.reminder_at)"
            />
            <span class="text-background-500 ml-auto text-xs">{{ formatDateTime(notitie.created_at) }}</span>
          </div>
        </template>
      </div>
    </div>

    <ConfirmDialog group="notitieDelete" />
  </div>
</template>

<script setup lang="ts">
  import { reactive, ref } from 'vue'
  import Textarea from 'primevue/textarea'
  import DatePicker from 'primevue/datepicker'
  import Button from 'primevue/button'
  import ConfirmDialog from 'primevue/confirmdialog'
  import { useConfirm } from 'primevue/useconfirm'
  import CustomTag from './CustomTag.vue'
  import { useNotities, type PortalNotitieEntityType } from '../composables/useNotities'
  import type { Notitie } from '../../../../types'

  const props = defineProps<{
    entityType: PortalNotitieEntityType
    entityId: string | null | undefined
  }>()

  const confirm = useConfirm()
  const { notities, loading, isOwn, createNotitie, updateNotitie, deleteNotitie } = useNotities(
    props.entityType,
    () => props.entityId,
  )

  const saving = ref(false)

  const draft = reactive<{ inhoud: string; reminder_at: Date | null }>({ inhoud: '', reminder_at: null })
  const editingId = ref<string | null>(null)
  const editDraft = reactive<{ inhoud: string; reminder_at: Date | null }>({ inhoud: '', reminder_at: null })

  function formatDateTime(value: string | null): string {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  async function handleCreate() {
    if (!draft.inhoud.trim()) return
    saving.value = true
    try {
      await createNotitie({ inhoud: draft.inhoud, reminder_at: draft.reminder_at ? draft.reminder_at.toISOString() : null })
      draft.inhoud = ''
      draft.reminder_at = null
    } catch {
      // toast handled in composable
    } finally {
      saving.value = false
    }
  }

  function startEdit(notitie: Notitie) {
    editingId.value = notitie.id
    editDraft.inhoud = notitie.inhoud
    editDraft.reminder_at = notitie.reminder_at ? new Date(notitie.reminder_at) : null
  }

  function cancelEdit() {
    editingId.value = null
  }

  async function handleUpdate(id: string) {
    if (!editDraft.inhoud.trim()) return
    saving.value = true
    try {
      await updateNotitie(id, {
        inhoud: editDraft.inhoud,
        reminder_at: editDraft.reminder_at ? editDraft.reminder_at.toISOString() : null,
      })
      editingId.value = null
    } catch {
      // toast handled in composable
    } finally {
      saving.value = false
    }
  }

  function confirmDelete(notitie: Notitie) {
    confirm.require({
      group: 'notitieDelete',
      message: 'Weet u zeker dat u deze notitie wilt verwijderen?',
      header: 'Notitie verwijderen',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger',
      accept: () => deleteNotitie(notitie.id),
    })
  }
</script>
